import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex, Spin, message } from 'antd';

import { UserAuth } from '../../../middleware/UserContext/UserContext.middleware';
import { useTranslation } from 'react-i18next';
import { useAdvertDetails } from '../../hooks/useAdvertDetails';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import AdvertWizard from '../../components/AdvertWizard/AdvertWizard.component';

const AdvertCreatePage: React.FC = () => {
  const { currentUser } = UserAuth();
  const modern = useAdvertDetails(currentUser, undefined, { loadOnMount: false });
  const advertModel = modern.advert;
  const advertModelLoading = modern.loading;
  const uploadFile = modern.uploadFile;
  const removeAttachment = modern.removeAttachment;
  const setTitle = modern.setTitle;
  const setDescription = modern.setDescription;
  const reorderAttachments = modern.reorderAttachments;
  const submitAdvert = modern.submitAdvert;
  
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { isAdmin, isResolved } = useCurrentUser();
  const [checkedTelegramAccounts, setCheckedTelegramAccounts] = useState<Record<string, boolean>>({});
  const [checkedChannels, setCheckedChannels] = useState<Record<string, boolean>>({});
  const [uploadQueue, setUploadQueue] = useState<{ file: File; onSuccess: () => void }[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const processQueue = async () => {
      if (uploadQueue.length > 0 && !isUploading) {
        setIsUploading(true);
        const { file, onSuccess } = uploadQueue[0];
        
        try {
          await uploadFile(file, (advertModel?.attachments?.length || 0));
          onSuccess();
        } catch (error: any) {
          const key = error?.message || 'upload-error';
          message.error(t(key, { ns: 'errors', limit: 10 }));
        } finally {
          setIsUploading(false);
          setUploadQueue(prevQueue => prevQueue.slice(1));
        }
      }
    };

    processQueue();
  }, [uploadQueue, isUploading, uploadFile, advertModel?.attachments?.length]);

  const uploadHandler = ({ file, onSuccess }: { file: File; onSuccess: () => void }) => {
    setUploadQueue(prevQueue => [...prevQueue, { file, onSuccess }]);
  };

  const submitHandler = async () => {
    if (!currentUser) {
      return navigate('/login');
    }

    setIsSubmitting(true);
    const selectedChannelUUIDs = checkedChannels 
      ? Object.keys(checkedChannels).filter((key) => checkedChannels[key]) 
      : [];

    const selectedTelegramAccountUUIDs = Object.keys(checkedTelegramAccounts).filter(key => checkedTelegramAccounts[key]);

    try {
      await submitAdvert(selectedChannelUUIDs, isAdmin && selectedTelegramAccountUUIDs.length > 0 ? selectedTelegramAccountUUIDs : undefined);
      navigate('/adverts');
    } catch (err: any) {
      const key = err?.message || 'advert-create-failed';
      message.error(t(key, { ns: 'errors', limit: 10 }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isResolved) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <AdvertWizard
      advertModel={advertModel}
      isAdvertLoading={advertModelLoading}
      checkedChannels={checkedChannels}
      isSubmitting={isSubmitting}
      pageTitle={t('advert-create-title', { ns: 'labels' })}
      submitButtonText={t('create', { ns: 'buttons' })}
      onTitleChange={setTitle}
      onDescriptionChange={setDescription}
      onUploadFile={uploadHandler}
      onRemoveAttachment={removeAttachment}
      onReorderAttachments={reorderAttachments}
      showTelegramAccountsStep={isAdmin}
      checkedTelegramAccounts={checkedTelegramAccounts}
      onCheckTelegramAccounts={setCheckedTelegramAccounts}
      onCheckChannels={setCheckedChannels}
      onSubmit={submitHandler}
    />
  );
};

export default AdvertCreatePage;